/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { collectionPlpViewMethods } from '@theme/collection-plp-view';
import { normalizeProductGroup, resolveUserContext } from '@theme/elevate-products';
import { scheduleRecommendations } from '@theme/elevate-recommendations-api';
import { getElevateClient } from '@theme/elevate-client';
import { StandardEvents } from '@shopify/events';

let quickAddProduct;

class ElevateCarousel extends HTMLElement {
  get cardTemplate() {
    return this.querySelector('[data-card-template] template');
  }
  get currencyCode() {
    return this.getAttribute('currency-code');
  }
  get locale() {
    return this.getAttribute('locale');
  }
  getLabel(name, fallback) {
    return this.getAttribute(name) || fallback;
  }

  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.addEventListener('elevate:recommendations', (event) => this.renderResult(event.detail), {
      signal: this.abortController.signal,
    });
    this.addEventListener(
      'click',
      (event) => {
        const link = event.target.closest('a');
        if (link?.hasAttribute('data-brand-link')) return;

        const card = link?.closest('[data-card]');
        const ticket = link?.dataset.elevateTicket || card?.dataset.collectionTicket;
        if (ticket && resolveUserContext('automatic') !== 'ANONYMOUS') {
          getElevateClient()
            .then((client) => client.notify.click(ticket))
            .catch(() => {});
        }
      },
      { signal: this.abortController.signal },
    );
    if (this.dataset.hideIfEmpty === 'true' && this.hidden && !window.Shopify?.designMode) {
      this.#setSectionHidden(true);
    }
    scheduleRecommendations();
    if (this.elevateResult) this.renderResult(this.elevateResult);
  }

  refreshFromThemeEditor() {
    this.elevateResult = undefined;
    this.hidden = false;
    this.#setSectionHidden(false);
    this.setAttribute('aria-busy', 'true');
    const status = this.querySelector('[data-status]');
    if (status) status.hidden = false;
    scheduleRecommendations();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.imageObserver?.disconnect();
    scheduleRecommendations();
  }

  renderResult({ list, error }) {
    const products =
      list?.visible === false ? [] : (list?.productGroups || []).map(normalizeProductGroup).filter(Boolean);
    const status = this.querySelector('[data-status]');
    const host = this.querySelector('[data-carousel-host]');
    const sliderTemplate = this.querySelector('[data-slider-template]');

    if (!status || !host || !sliderTemplate) return;

    host.replaceChildren();
    this.removeAttribute('aria-busy');
    if (error || !products.length) {
      const hide = this.dataset.hideIfEmpty !== 'false' && !window.Shopify?.designMode;
      this.hidden = hide;
      this.#setSectionHidden(hide);
      status.hidden = hide;
      status.textContent = error || this.getLabel('no-recommendations-text', 'No recommendations available.');
      return;
    }
    this.hidden = false;
    this.#setSectionHidden(false);
    status.hidden = true;

    const fragment = document.importNode(sliderTemplate.content, true);
    const scroller = fragment.querySelector('slideshow-slides');
    const slideshow = fragment.querySelector('slideshow-component');

    if (!scroller || !slideshow) return;

    this.imageObserver?.disconnect();
    // Native lazy loading stalls for images assembled inside the slider templates,
    // so off-screen slides are switched to eager loading as they approach the viewport.
    this.imageObserver = new IntersectionObserver(
      (entries, observer) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          for (const image of entry.target.querySelectorAll('img[loading="lazy"]')) {
            image.loading = 'eager';
          }

          observer.unobserve(entry.target);
        }
      },
      { rootMargin: '200px 100% 200px 100%' },
    );

    products.forEach((product, index) => {
      const slide = document.createElement('slideshow-slide');
      slide.setAttribute('ref', 'slides[]');
      slide.setAttribute('aria-hidden', index ? 'true' : 'false');
      slide.className = 'resource-list__slide';
      slide.style.setProperty('--slideshow-timeline', `--slide-${index}`);
      const card = collectionPlpViewMethods.createProductCard.call(this, product);
      // The shared PLP renderer returns an li; retain valid list semantics inside each slide.
      const listElement = document.createElement('ul');
      listElement.className = 'elevate-carousel__card-list';
      listElement.append(card);
      this.imageObserver.observe(slide);
      card.elevateProduct = product;
      card.querySelectorAll('.collection-plp__swatches a').forEach((link, index) => {
        link.dataset.elevateTicket = product.colorOptions[index]?.ticket || '';
      });
      slide.append(listElement);
      scroller.append(slide);
    });
    slideshow.style.setProperty('--slideshow-timeline', products.map((_, index) => `--slide-${index}`).join(', '));
    host.append(fragment);
  }

  /**
   * Hides the Shopify section wrapper when the PDP carousel is configured to disappear without products.
   * @param {boolean} hide
   */
  #setSectionHidden(hide) {
    if (this.dataset.hideIfEmpty !== 'true') return;

    const section = this.closest('.shopify-section');
    if (section) section.hidden = hide;
  }
}

if (!customElements.get('elevate-carousel')) customElements.define('elevate-carousel', ElevateCarousel);

document.addEventListener(
  'click',
  (event) => {
    if (event.target.closest('quick-add-component')) {
      quickAddProduct = event.target.closest('[data-card]')?.elevateProduct;
    }
  },
  true,
);

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const variantId = event.target.closest?.('quick-add-dialog')
    ? event.target.querySelector('[name="id"]')?.value
    : null;
  const ticket = quickAddProduct?.variants.find((variant) => variant.shopifyVariantId === variantId)?.ticket;
  event.promise
    ?.then(({ detail }) => {
      if (detail?.didError) return;
      if (ticket && resolveUserContext('automatic') !== 'ANONYMOUS')
        getElevateClient()
          .then((client) => client.notify.addToCart(ticket))
          .catch(() => {});
      if (document.querySelector('elevate-carousel[data-context="cart"]')) scheduleRecommendations();
    })
    .catch(() => {});
});
document.addEventListener('visitorConsentCollected', () => scheduleRecommendations(true));

document.addEventListener('shopify:section:load', (event) => {
  const carousels = event.target.matches?.('elevate-carousel')
    ? [event.target]
    : [...(event.target.querySelectorAll?.('elevate-carousel') || [])];
  carousels.forEach((carousel) => carousel.refreshFromThemeEditor());
});
